/**
 * PV2 Dynamic Panels: fork of product-dynamic-panels.js scoped to
 * .product-visualizer-v2 containers so it never conflicts with the
 * standalone product-dynamic-panels section.
 */
(function () {
  const PV2_ROOT = '.product-visualizer-v2';

  function pv2QueryAll(selector) {
    const results = [];
    for (const root of document.querySelectorAll(PV2_ROOT)) {
      for (const el of root.querySelectorAll(selector)) {
        results.push(el);
      }
    }
    return results;
  }
  function findSlider(el) {
    return el.closest && el.closest('[is="accordion-slider"]');
  }

  function isBarAccordion(el) {
    const accordion = el.closest('.pv2-dynamic-panels-accordion');
    return accordion && accordion.classList.contains('pv2-dynamic-panels-accordion--bar');
  }

  function goToSlide(slider, index) {
    if (slider && typeof slider.goToSlide === 'function') {
      slider.goToSlide(parseInt(index, 10));
    }
  }

  // ─── Scroll animation ────────────────────────────────────────────────────────

  // Use CssSwiper.animateScroll (rAF easing loop, iOS-reliable) when available.
  // This is a static method on the class; we access it via customElements.get()
  // so we don't need to import the module or wrap content in a <css-swiper> element.
  function pdpAnimateScroll(list, targetX) {
    const CssSwiper = customElements.get('css-swiper');
    if (CssSwiper && typeof CssSwiper.animateScroll === 'function') {
      CssSwiper.animateScroll(list, targetX, 300);
    } else {
      list.scrollTo({ left: targetX, behavior: 'smooth' });
    }
  }

  function itemScrollTarget(list, item) {
    var sm = parseFloat(getComputedStyle(item).scrollMarginLeft) || 0;
    return item.getBoundingClientRect().left - list.getBoundingClientRect().left - list.clientLeft + list.scrollLeft - sm;
  }

  // After native scroll settles, snap to whichever item is closest.
  // Prevents the carousel resting half-way between slides on iOS.
  function snapToClosest(list) {
    if (!list || !list.children.length) return;
    const items = Array.from(list.children);
    const scrollLeft = list.scrollLeft;
    let closestItem = items[0];
    let minDistance = Math.abs(scrollLeft - itemScrollTarget(list, closestItem));
    items.forEach(function (item) {
      const distance = Math.abs(scrollLeft - itemScrollTarget(list, item));
      if (distance < minDistance) {
        closestItem = item;
        minDistance = distance;
      }
    });
    pdpAnimateScroll(list, itemScrollTarget(list, closestItem));
  }

  // ─── Active-state helpers ────────────────────────────────────────────────────

  function syncIndicatorBar(root, idx) {
    root.querySelectorAll('.pv2-dynamic-panels-accordion__indicator-bar-item').forEach(function (item, i) {
      item.classList.toggle('pv2-dynamic-panels-accordion__indicator-bar-item--active', i === idx);
      item.setAttribute('aria-selected', i === idx);
    });
  }

  function syncMediaLabels(slider, idx) {
    slider.querySelectorAll('.pv2-dynamic-panels-media__label').forEach(function (el) {
      el.setAttribute('data-active', el.getAttribute('data-accordion-index') === String(idx));
    });
  }

  // Set details.open so the active slide expands (AccordionDetails runs height
  // animation and shows content). Required on both desktop (accordion) and
  // mobile (swiper): without it, non-active slides stay collapsed and content
  // doesn’t show when you swipe or tap the bar.
  function syncDetailsOpen(root, idx) {
    if (window.matchMedia('(max-width: 809px)').matches) {
      const rootIsNumbered = root.classList.contains('pv2-dynamic-panels-accordion--numbered') ||
        !!root.querySelector('.pv2-dynamic-panels-accordion--numbered');
      if (rootIsNumbered) return;
    }
    root.querySelectorAll('[data-slide-accordion]').forEach(function (item, i) {
      const details = item.querySelector('details');
      if (details) details.open = i === idx;
    });
  }

  // ─── Mobile list height sync ────────────────────────────────────────────────

  function syncListHeight(list, idx) {
    var wrap = list ? list.closest('.pv2-dynamic-panels-accordion__list-wrap') : null;
    if (!list || !window.matchMedia('(max-width: 809px)').matches || isBarAccordion(list)) {
      if (wrap) wrap.style.height = '';
      return;
    }
    if (list._pdpProgrammaticScroll) return;
    var item = list.children[idx];
    if (!item) return;
    var h = item.offsetHeight;
    if (!h) return;
    if (wrap) wrap.style.height = h + 'px';
  }

  // ─── Full sync (click handlers + initial load) ───────────────────────────────

  function scrollToItem(list, idx) {
    var item = list.children[idx];
    if (!item) return;
    var targetX = itemScrollTarget(list, item);
    list._pdpProgrammaticScroll = true;
    if (list._pdpRatioMap) list._pdpRatioMap.clear();
    var wrap = list.closest('.pv2-dynamic-panels-accordion__list-wrap');
    var h = item.offsetHeight;
    if (h && wrap) wrap.style.height = h + 'px';
    list.style.scrollSnapType = 'none';
    clearTimeout(list._pdpScrollEndTimeout);
    pdpAnimateScroll(list, targetX);
    clearTimeout(list._pdpProgrammaticScrollTimeout);
    list._pdpProgrammaticScrollTimeout = setTimeout(function () {
      list._pdpProgrammaticScroll = false;
      syncListHeight(list, idx);
      requestAnimationFrame(function () {
        list.style.scrollSnapType = '';
      });
    }, 400);
  }

  function setActiveInPanel(slider, index, options) {
    if (!slider) return;
    options = options || {};
    const idx = parseInt(index, 10);
    syncMediaLabels(slider, idx);
    syncIndicatorBar(slider, idx);
    syncDetailsOpen(slider, idx);
    var list = slider.querySelector('.pv2-dynamic-panels-accordion__list');
    if (list) {
      syncListHeight(list, idx);
      if (options.scrollAccordionToIndex) scrollToItem(list, idx);
    }
  }

  function setActiveInAccordionOnly(accordion, index, options) {
    options = options || {};
    const idx = parseInt(index, 10);
    syncIndicatorBar(accordion, idx);
    syncDetailsOpen(accordion, idx);
    var list = accordion.querySelector('.pv2-dynamic-panels-accordion__list');
    if (list) {
      syncListHeight(list, idx);
      if (options.scrollAccordionToIndex) scrollToItem(list, idx);
    }
  }

  // ─── Click handlers ──────────────────────────────────────────────────────────

  function handleLabelClick(e) {
    const btn = e.target.closest('.pv2-dynamic-panels-media__label[data-accordion-index]');
    if (!btn || !btn.closest(PV2_ROOT)) return;
    const index = btn.getAttribute('data-accordion-index');
    const slider = findSlider(btn);
    if (slider) {
      e.preventDefault();
      goToSlide(slider, index);
      setActiveInPanel(slider, index, { scrollAccordionToIndex: true });
    }
  }

  function handleIndicatorBarClick(e) {
    const btn = e.target.closest('.pv2-dynamic-panels-accordion__indicator-bar-item[data-accordion-index]');
    if (!btn || !btn.closest(PV2_ROOT)) return;
    e.preventDefault();
    const index = btn.getAttribute('data-accordion-index');
    const slider = findSlider(btn);
    const accordion = btn.closest('.pv2-dynamic-panels-accordion');
    if (slider) {
      goToSlide(slider, index);
      setActiveInPanel(slider, index, { scrollAccordionToIndex: true });
    } else if (accordion) {
      setActiveInAccordionOnly(accordion, index, { scrollAccordionToIndex: true });
    }
  }

  // ─── Slider → accordion sync ─────────────────────────────────────────────────

  function syncActiveFromSlider(slider) {
    const active = slider.querySelector('[data-slide-accordion][data-slide-active="true"]');
    if (active) {
      const idx = Array.prototype.indexOf.call(slider.querySelectorAll('[data-slide-accordion]'), active);
      if (idx !== -1) setActiveInPanel(slider, idx);
    }
  }

  // ─── Scroll handler (mobile swipe) ───────────────────────────────────────────

  function getIndexFromScrollLeft(list) {
    if (!list || !list.children.length) return 0;
    const items = Array.from(list.children);
    const scrollLeft = list.scrollLeft;
    let closestIdx = 0;
    let minDistance = Math.abs(scrollLeft - itemScrollTarget(list, items[0]));
    items.forEach(function (item, i) {
      const distance = Math.abs(scrollLeft - itemScrollTarget(list, item));
      if (distance < minDistance) {
        closestIdx = i;
        minDistance = distance;
      }
    });
    return closestIdx;
  }

  function handleAccordionListScroll(e) {
    const list = e.target;
    if (!list.classList.contains('pv2-dynamic-panels-accordion__list')) return;
    if (!list.closest(PV2_ROOT)) return;
    if (window.matchMedia('(min-width: 810px)').matches) return;
    if (isBarAccordion(list)) return;
    if (list.isAnimating || list._pdpProgrammaticScroll) return;

    const slider = findSlider(list);
    const accordion = list.closest('.pv2-dynamic-panels-accordion');

    clearTimeout(list._pdpScrollEndTimeout);
    list._pdpScrollEndTimeout = setTimeout(function () {
      if (list.isAnimating) return;
      const idx = getIndexFromScrollLeft(list);
      syncListHeight(list, idx);
      if (slider) {
        goToSlide(slider, idx);
        syncMediaLabels(slider, idx);
        syncIndicatorBar(slider, idx);
        syncDetailsOpen(slider, idx);
      } else if (accordion) {
        syncIndicatorBar(accordion, idx);
        syncDetailsOpen(accordion, idx);
      }
    }, 100);
  }

  // ─── Mobile IntersectionObserver for real-time active state ────────────────

  function setupMobileItemObserver(list) {
    if (!window.IntersectionObserver || !window.matchMedia('(max-width: 809px)').matches) return;
    if (isBarAccordion(list)) return;
    if (list._pdpItemObserver) return;

    var items = list.querySelectorAll('.pv2-dynamic-panels-accordion__item');
    if (items.length <= 1) return;

    var ratioMap = new Map();
    list._pdpRatioMap = ratioMap;

    var observer = new IntersectionObserver(function (entries) {
      if (list._pdpProgrammaticScroll) return;

      for (var i = 0; i < entries.length; i++) {
        ratioMap.set(entries[i].target, entries[i].intersectionRatio);
      }

      var bestItem = null;
      var bestRatio = 0;
      ratioMap.forEach(function (ratio, item) {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestItem = item;
        }
      });

      items.forEach(function (item) {
        item.setAttribute('data-slide-active', item === bestItem ? 'true' : 'false');
      });
    }, { root: list, threshold: [0, 0.25, 0.5, 0.75, 1] });

    items.forEach(function (item) { observer.observe(item); });
    list._pdpItemObserver = observer;
  }

  // ─── Initialisation ──────────────────────────────────────────────────────────

  // On mobile, open all details via setAttribute so the browser shows content.
  // We avoid setting .open (which would run AccordionDetails transition).
  function openAllDetailsOnMobile(container) {
    if (!container || !window.matchMedia('(max-width: 809px)').matches) return;
    if (container.classList.contains('pv2-dynamic-panels-accordion--bar') ||
        container.querySelector('.pv2-dynamic-panels-accordion--bar')) return;
    container.querySelectorAll('.pv2-dynamic-panels-accordion__list [data-slide-accordion] details').forEach(function (details) {
      details.setAttribute('open', '');
    });
  }

  // Only the first <details> has the open attribute in the HTML. On desktop, ensure
  // non-first items are closed so _open = false and the open attribute is removed,
  // without running the Motion animation. The CSS already hides their content via
  // the :not(.accordion-details--open) rule, so there is no visual flash.
  // On mobile, openAllDetailsOnMobile adds the open attribute back for the swiper.
  function silentlyCloseNonFirstOnDesktop(container) {
    if (!container) return;
    if (window.matchMedia('(max-width: 809px)').matches) return;
    var items = container.querySelectorAll('[data-slide-accordion]');
    Array.prototype.forEach.call(items, function (item, i) {
      if (i === 0) return;
      var details = item.querySelector('details');
      if (!details) return;
      // Bypass the AccordionDetails setter (which would animate) and reset state directly.
      if ('_open' in details) details._open = false;
      details.removeAttribute('open');
      details.classList.remove('accordion-details--open');
    });
  }

  function observeSliders() {
    pv2QueryAll('.pv2-dynamic-panels-panel--accordion-media-sync').forEach(function (slider) {
      if (slider._pdpObserved) return;
      slider._pdpObserved = true;
      syncActiveFromSlider(slider);
      setActiveInPanel(slider, 0);
      openAllDetailsOnMobile(slider);
      silentlyCloseNonFirstOnDesktop(slider);
      var observer = new MutationObserver(function () {
        syncActiveFromSlider(slider);
      });
      observer.observe(slider, { attributes: true, attributeFilter: ['data-slide-active'], subtree: true });
      var list = slider.querySelector('.pv2-dynamic-panels-accordion__list');
      if (list) {
        list._pdpScrollObserved = true;
        list.addEventListener('scroll', handleAccordionListScroll, { passive: true });
        setupMobileItemObserver(list);
        syncListHeight(list, 0);
      }
    });
    pv2QueryAll('.pv2-dynamic-panels-accordion__list').forEach(function (list) {
      if (list._pdpScrollObserved) return;
      list._pdpScrollObserved = true;
      list.addEventListener('scroll', handleAccordionListScroll, { passive: true });
      setupMobileItemObserver(list);
      syncListHeight(list, 0);
      var accordion = list.closest('.pv2-dynamic-panels-accordion');
      openAllDetailsOnMobile(accordion);
      silentlyCloseNonFirstOnDesktop(accordion);
    });
  }

  function init() {
    document.removeEventListener('click', handleLabelClick, true);
    document.removeEventListener('click', handleIndicatorBarClick, true);
    document.addEventListener('click', handleLabelClick, true);
    document.addEventListener('click', handleIndicatorBarClick, true);
    observeSliders();
  }

  // Re-sync details open/closed state whenever the viewport crosses the mobile breakpoint.
  // silentlyCloseNonFirstOnDesktop removes the open attribute on desktop; openAllDetailsOnMobile
  // restores it on mobile. Without this, loading on desktop then resizing to mobile leaves
  // non-first slides with no open attribute and blank content.
  window.matchMedia('(max-width: 809px)').addEventListener('change', function (e) {
    pv2QueryAll('.pv2-dynamic-panels-accordion').forEach(function (accordion) {
      var list = accordion.querySelector('.pv2-dynamic-panels-accordion__list');
      if (e.matches) {
        openAllDetailsOnMobile(accordion);
        if (list) {
          setupMobileItemObserver(list);
          syncListHeight(list, 0);
        }
      } else {
        silentlyCloseNonFirstOnDesktop(accordion);
        syncIndicatorBar(accordion, 0);
        if (list) list.style.height = '';
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
